import type { ReactNode } from "react";

function cx(...values: Array<unknown>): string {
  return values.filter((value): value is string => typeof value === "string" && value.length > 0).join(" ");
}

export type StatusBadgeTone = "neutral" | "success" | "warning" | "danger" | "info";

export function StatusBadge({
  children,
  tone = "neutral",
  dot = false,
  className,
}: {
  children: ReactNode;
  tone?: StatusBadgeTone;
  dot?: boolean;
  className?: string;
}) {
  return <span className={cx("cd-status-badge", className)} data-tone={tone}>
    {dot && <span className="cd-status-badge-dot" aria-hidden="true" />}
    <span>{children}</span>
  </span>;
}

export function OperationalWorkspace({
  list,
  detail,
  className,
}: {
  list: ReactNode;
  detail?: ReactNode;
  className?: string;
}) {
  return <div className={cx("cd-operational-workspace", className)} data-has-detail={detail ? "true" : "false"}>
    <div className="cd-operational-workspace-list">{list}</div>
    {detail && <div className="cd-operational-workspace-detail">{detail}</div>}
  </div>;
}

export function OperationalList({
  children,
  className,
  ariaLabel,
}: {
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}) {
  return <div className={cx("cd-operational-list", className)} role="list" aria-label={ariaLabel}>{children}</div>;
}

export function OperationalRow({
  children,
  selected = false,
  onSelect,
  className,
  buttonClassName,
  ariaLabel,
  disabled = false,
}: {
  children: ReactNode;
  selected?: boolean;
  onSelect: () => void;
  className?: string;
  buttonClassName?: string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  return <div className={cx("cd-operational-row", selected && "is-selected", className)} role="listitem" data-selected={selected ? "true" : "false"}>
    <button
      type="button"
      className={cx("cd-operational-row-main", buttonClassName)}
      aria-pressed={selected}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onSelect}
    >{children}</button>
  </div>;
}

export function OperationalDetail({
  children,
  className,
  sticky = false,
  ariaLabel,
}: {
  children: ReactNode;
  className?: string;
  sticky?: boolean;
  ariaLabel?: string;
}) {
  return <div
    className={cx("cd-operational-detail", className)}
    data-sticky={sticky ? "true" : "false"}
    role={ariaLabel ? "region" : undefined}
    aria-label={ariaLabel}
  >{children}</div>;
}
